import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Badge, Box, Collapse, Group, Progress, Stack, Text, UnstyledButton, useMantineColorScheme } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { SectionCompletionDto } from '../types/profileCompletion';
import { getScoreColor, getSectionIcon } from '../utils/scoreUtils';

interface SectionCardProps {
    section: SectionCompletionDto;
}

export const SectionCard = ({ section }: SectionCardProps) => {
    const [open, setOpen] = useState(false);
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { t } = useTranslation();

    const color = getScoreColor(section.normalizedScore);
    const pct = Math.round(section.normalizedScore * 100);
    const weightPct = Math.round(section.weight * 100);
    const hasIssues = section.rules.some((r) => !r.passed);
    const icon = getSectionIcon(section.sectionName);

    return (
        <Box
            style={{
                border: `1.5px solid ${open ? color.brandColor + '55' : isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
                borderRadius: 12,
                overflow: 'hidden',
                background: isDark ? 'var(--mantine-color-dark-6)' : 'white',
                transition: 'border-color 0.2s ease',
            }}
        >
            <UnstyledButton
                onClick={() => setOpen((prev) => !prev)}
                style={{ width: '100%', padding: '14px 16px' }}
            >
                <Group gap={12} wrap="nowrap">
                    <Text style={{ fontSize: 20, flexShrink: 0 }}>{icon}</Text>

                    <Box style={{ flex: 1, minWidth: 0 }}>
                        <Group gap={8} mb={4} wrap="nowrap">
                            <Text size="sm" fw={700} truncate="end">
                                {section.sectionName}
                            </Text>
                            {hasIssues && (
                                <Badge size="xs" color="yellow" variant="light">
                                    {t('PROFILE_COMPLETION_ACTION_NEEDED')}
                                </Badge>
                            )}
                        </Group>
                        <Progress
                            value={pct}
                            color={color.mantineColor}
                            size="xs"
                            radius="xl"
                        />
                    </Box>

                    <Box ta="right" style={{ flexShrink: 0, minWidth: 52 }}>
                        <Text size="xs" c="dimmed">{t('PROFILE_COMPLETION_WEIGHT_LABEL')}</Text>
                        <Text size="sm" fw={700} c="dimmed">{weightPct}%</Text>
                    </Box>

                    <ChevronDownIcon
                        style={{
                            width: 16,
                            height: 16,
                            color: 'var(--mantine-color-dimmed)',
                            flexShrink: 0,
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                        }}
                    />
                </Group>
            </UnstyledButton>

            {/* This section is using section.Rules that is json with all the information */}
            <Collapse in={open}>
                <Box
                    px={16}
                    pb={14}
                    style={{
                        borderTop: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-2)'}`,
                    }}
                >
                    <Text
                        size="xs"
                        fw={600}
                        c="dimmed"
                        mt={12}
                        mb={10}
                        style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}
                    >
                        {t('PROFILE_COMPLETION_CHECKS_HEADER', {
                            passed: String(section.rules.filter((r) => r.passed).length),
                            total: String(section.rules.length),
                        })}
                    </Text>

                    <Stack gap={8}>
                        {section.rules.map((rule) => (
                            <Box
                                key={rule.ruleId}
                                p="10px 12px"
                                style={{
                                    display: 'flex',
                                    gap: 10,
                                    alignItems: 'flex-start',
                                    background: rule.passed
                                        ? isDark ? 'rgba(16,185,129,0.1)' : '#f0fdf4'
                                        : isDark ? 'rgba(245,158,11,0.1)' : '#fffbeb',
                                    border: `1px solid ${rule.passed
                                        ? isDark ? 'rgba(16,185,129,0.3)' : '#bbf7d0'
                                        : isDark ? 'rgba(245,158,11,0.3)' : '#fde68a'
                                        }`,
                                    borderRadius: 8,
                                }}
                            >
                                <Box
                                    style={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        flexShrink: 0,
                                        background: rule.passed ? '#10b981' : '#f59e0b',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 11,
                                        color: 'white',
                                        fontWeight: 800,
                                        marginTop: 1,
                                    }}
                                >
                                    {rule.passed ? '✓' : '!'}
                                </Box>

                                <Box style={{ flex: 1, minWidth: 0 }}>
                                    <Text size="sm" fw={700} mb={2}>
                                        {rule.ruleName}
                                    </Text>
                                    <Text size="xs" c="dimmed" style={{ lineHeight: 1.5 }}>
                                        {rule.reasoning}
                                    </Text>

                                    {rule.tip && (
                                        <Box
                                            mt={6}
                                            p="6px 9px"
                                            style={{
                                                background: isDark ? 'rgba(245,158,11,0.1)' : '#fef3c7',
                                                borderRadius: 6,
                                                borderLeft: '3px solid #f59e0b',
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            <Text size="sm" c={isDark ? 'yellow.4' : 'brand.5'}>
                                                💡 {rule.tip}
                                            </Text>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            </Collapse>
        </Box>
    );
};


